import { Flex } from "antd";
import { CryptoIcon, DisplayAmount, getCryptoIconRound, TxButton, WhiteBoxBlock } from "ui";
import { SyncBalanceButton, ReturnNativesButton } from "components";
import { ReturnTokensButton } from "components/ReturnTokens";
import { EXPLORER_LINK_TYPE, getBlockchainExplorerLink, isStatusIncluded, useBalanceEvents } from "utils";
import { BidEntity, NETWORK_TEXT, BID_REQUEST_STATUS_ENUM, BalanceEventData, BALANCE_ENUM } from "interfaces";
import { UserStore } from "stores/UserStore";
import { Divider } from "antd";
import styles from "./transitWallet.module.css";
import { observer } from "mobx-react-lite";
import { RubBalanceBlock } from "./RubBalanceBlock/RubBalanceBlock";
import { DIRECT_PAYMENT_CUSTOMER_LP } from "utils/envConstants";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface TransitWalletBlockProps {
  bid: BidEntity;
  user: UserStore;
}

export const TransitWalletBlock = observer(({ bid, user }: TransitWalletBlockProps) => {
  const { transitWallet, network, balance, currency, status } = bid;
  const { subscribeToBalance, isConnected } = useBalanceEvents();
  const [tokenAmountBalance, setTokenAmountBalance] = useState<BalanceEventData | null>(null);
  const [nativeAmountBalance, setNativeAmountBalance] = useState<BalanceEventData | null>(null);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribeToken = subscribeToBalance(BALANCE_ENUM.TOKEN, (event) => {
      if (event.requestNumber === bid.id) {
        setTokenAmountBalance(event);
      }
    });
    const unsubscribeNative = subscribeToBalance(BALANCE_ENUM.NATIVE, (event) => {
      if (event.requestNumber === bid.id) {
        setNativeAmountBalance(event);
      }
    });

    return () => {
      unsubscribeToken();
      unsubscribeNative();
    };
  }, [isConnected, subscribeToBalance, bid.id]);

  const { isCustomer, isRiskManager, isSeniorOperator, isJuniorOperator, isJuniorLP, isSeniorLP, isOperator, isLPRiskManager } =
    user.roles;

  const isLpProvider = isJuniorLP || isSeniorLP || isLPRiskManager;
  // const canDownloadReportRole = isRiskManager || isSeniorOperator || isJuniorOperator;

  const showReturnNatives =
    (isRiskManager || isSeniorOperator) &&
    status !== BID_REQUEST_STATUS_ENUM.FUNDING &&
    (balance ? +balance.token === 0 && +balance.native > 0 : false);

  const showReturnTokens =
    isRiskManager &&
    transitWallet &&
    isStatusIncluded(status, [
      BID_REQUEST_STATUS_ENUM.CANCELED,
      BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDED,
      BID_REQUEST_STATUS_ENUM.COMPLETED,
      BID_REQUEST_STATUS_ENUM.AML_FAILED,
      BID_REQUEST_STATUS_ENUM.AML_FAILED_REFUNDED,
    ]) &&
    !isOperator;

  const showTransitWalletLp =
    isStatusIncluded(status, [
      BID_REQUEST_STATUS_ENUM.FUNDING,
      BID_REQUEST_STATUS_ENUM.FUNDED,
      BID_REQUEST_STATUS_ENUM.COMPLETED,
      BID_REQUEST_STATUS_ENUM.CANCELED,
      BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDED,
      BID_REQUEST_STATUS_ENUM.AML_FAILED,
      BID_REQUEST_STATUS_ENUM.AML_FAILED_REFUNDED,
    ]) && isLpProvider;

  const shouldShowTransitWallet = (transitWallet && !isLpProvider) || (showTransitWalletLp && transitWallet && network);

  if (!shouldShowTransitWallet) {
    return null;
  }

  const nativeAmount = nativeAmountBalance?.amount ?? balance?.native;
  const tokenAmount = tokenAmountBalance?.amount ?? balance?.token;

  return (
    <Flex vertical gap="small" style={{ width: "100%" }}>
      <WhiteBoxBlock className={`bg-basic-200 full-width ${styles.transitWalletBlockPadding}`}>
        <Flex vertical>
          <Flex align="center" gap="small">
            <CryptoIcon currency={network || ""} size="4xl" />
            <TxButton
              isWalletAddress
              text={transitWallet || ""}
              title={`Транзитный кошелек ${network ? NETWORK_TEXT[network] : ""}`}
              showCopyBtn
              href={getBlockchainExplorerLink(network, transitWallet || "", EXPLORER_LINK_TYPE.address)}
              infoBlock
              isBorderedButton
            />
          </Flex>
          <Divider className="margin-top-sm margin-bottom-sm" />
          <Flex>
            {balance && Object.keys(balance).length > 0 && (
              <Flex justify="space-between" gap="large" align="center" className="full-width" wrap="wrap">
                <Flex vertical gap="small" className="flex-shrink-0">
                  <Flex align="center" gap="8px">
                    {getCryptoIconRound(currency as string, "xl")}
                    <DisplayAmount amount={+tokenAmount} currency={currency as string} isCurrencyGrey />
                  </Flex>

                  {!isCustomer && (
                    <Flex align="center" gap="8px">
                      {getCryptoIconRound(network as string, "xl")}
                      <DisplayAmount amount={+nativeAmount} decimals={4} currency={network as string} isCurrencyGrey />
                    </Flex>
                  )}
                </Flex>
                <Flex gap="4px" align="center">
                  {/* {canDownloadReportRole && <SyncBalanceButton />} */}
                  {showReturnNatives && <ReturnNativesButton />}
                  {showReturnTokens && <ReturnTokensButton />}
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
      </WhiteBoxBlock>
      {!DIRECT_PAYMENT_CUSTOMER_LP && <RubBalanceBlock bid={bid} />}
    </Flex>
  );
});
